import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, CardContent, Typography, TextField, Select, MenuItem,
  FormControl, InputLabel, Chip, IconButton, Tooltip, Grid, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  LinearProgress, Alert, Divider
} from '@mui/material';
import {
  Add, Search, FilterList, Download, Visibility, Edit, WarningAmber, CheckCircle, Error, Upload
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contractsApi, reportsApi } from '../../services/api';
import ImportContractsDialog from './ImportContractsDialog';
import { format, parseISO } from 'date-fns';
import type { Contract } from '../../types';
import { useUser } from '../../context/UserContext';

const CONTRACT_TYPES = ['MSA', 'SOW', 'SLA', 'NDA', 'IRU', 'VENDOR', 'CUSTOMER', 'OTHER'];
const CONTRACT_STATUSES = ['draft', 'active', 'expired', 'terminated', 'suspended'];

function StatusChip({ status }: { status: string }) {
  const map: Record<string, any> = {
    active: { color: 'success', label: 'Active' },
    expired: { color: 'error', label: 'Expired' },
    draft: { color: 'default', label: 'Draft' },
    terminated: { color: 'error', label: 'Terminated' },
    suspended: { color: 'warning', label: 'Suspended' }
  };
  const cfg = map[status] || { color: 'default', label: status };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

function ExpirationIndicator({ days }: { days?: number }) {
  if (days === undefined || days === null) return <Typography variant="body2" color="text.disabled">-</Typography>;
  if (days < 0) return <Chip icon={<Error />} label="Expired" color="error" size="small" variant="outlined" />;
  if (days <= 30) return <Chip icon={<WarningAmber />} label={`${days}d`} color="error" size="small" />;
  if (days <= 90) return <Chip icon={<WarningAmber />} label={`${days}d`} color="warning" size="small" />;
  return <Typography variant="body2">{days}d</Typography>;
}

export default function ContractList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { effectiveRole } = useUser();
  // Viewers get a read-only experience: hide Import, New Contract, and the Edit pencil.
  const canEdit = effectiveRole !== 'viewer';

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [contractType, setContractType] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [importOpen, setImportOpen] = useState(false);

  const loadContracts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = { page: page + 1, limit: rowsPerPage };
      if (search) params.search = search;
      if (status) params.status = status;
      if (contractType) params.contract_type = contractType;

      const res = await contractsApi.list(params);
      setContracts(res.data.contracts);
      setTotal(res.data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [search, status, contractType, page, rowsPerPage]);

  useEffect(() => { loadContracts(); }, [loadContracts]);

  const handleExport = async () => {
    try {
      const res = await reportsApi.exportContracts();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `contracts-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const formatDate = (d?: string) => d ? format(parseISO(d), 'MMM d, yyyy') : '-';

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5">Contracts</Typography>
          <Typography variant="body2" color="text.secondary">{total} total contracts</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<Download />} onClick={handleExport} size="small">
            Export CSV
          </Button>
          {canEdit && (
            <>
              <Button variant="outlined" startIcon={<Upload />} onClick={() => setImportOpen(true)}>
                Import
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/contracts/new')}>
                New Contract
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth size="small" placeholder="Search contracts, counterparties..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={status} label="Status" onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
                  <MenuItem value="">All Statuses</MenuItem>
                  {CONTRACT_STATUSES.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={contractType} label="Type" onChange={(e) => { setContractType(e.target.value); setPage(0); }}>
                  <MenuItem value="">All Types</MenuItem>
                  {CONTRACT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={1} textAlign="right">
              <Tooltip title="Clear filters">
                <IconButton size="small" onClick={() => { setSearch(''); setStatus(''); setContractType(''); setPage(0); }}>
                  <FilterList />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {/* Table */}
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Contract</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Counterparty</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Effective</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Days Left</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Obligations</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 80 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contracts.map((contract) => (
                <TableRow
                  key={contract.id}
                  hover
                  sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                  onClick={() => navigate(`/contracts/${contract.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                      {contract.contract_name}
                    </Typography>
                    {contract.contract_number && (
                      <Typography variant="caption" color="text.secondary">#{contract.contract_number}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{contract.counterparty_name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{contract.counterparty_type}</Typography>
                  </TableCell>
                  <TableCell><Chip label={contract.contract_type} size="small" variant="outlined" /></TableCell>
                  <TableCell><StatusChip status={contract.status} /></TableCell>
                  <TableCell><Typography variant="body2">{formatDate(contract.effective_date)}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{formatDate(contract.expiration_date)}</Typography></TableCell>
                  <TableCell><ExpirationIndicator days={contract.days_until_expiration} /></TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} alignItems="center">
                      <Typography variant="body2">{contract.obligation_count || 0}</Typography>
                      {(contract.overdue_obligations || 0) > 0 && (
                        <Chip label={`${contract.overdue_obligations} overdue`} color="error" size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box display="flex">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => navigate(`/contracts/${contract.id}`)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => navigate(`/contracts/${contract.id}/edit`)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && contracts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No contracts found</Typography>
                    {canEdit && (
                      <Button sx={{ mt: 1 }} variant="contained" onClick={() => navigate('/contracts/new')}>
                        Create First Contract
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        />
      </Card>

      <ImportContractsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={loadContracts}
      />
    </Box>
  );
}
